import { PaymentGateway } from '@src/types/onboarding.ts';

import { PaymentFormValues } from './packages';

export type BillingReceiptsReq = {
  start_date: string | null;
  end_date: string | null;
  search_words: string | null;
};

export enum BillingActionType {
  Canceled = 'canceled',
  Renew = 'renew',
}

export enum BillingStatus {
  Active = 'active',
  Canceled = 'canceled',
  Expired = 'incomplete_expired',
}

export type ProductBilling = Partial<{
  id: number;
  created_at: string;
  type: string;
  display_name_app: string;
  display_name_web: string;
  title: string;
}>;

export type TBillingTable = Partial<{
  id: number;
  user_id: number;
  plan_id: string;
  product_id: number;
  subscription_id: string;
  payment_system: PaymentGateway;
  invoice_status: BillingStatus;
  subscription_status: BillingStatus;
  price: number;
  invoice_id: number;
  begin_date: string;
  expire_date: string;
  canceled_at: string | null;
  charge_id: string | null;
  product: ProductBilling;
}>;

export type TAccountReceiptsTable = Partial<{
  id: string;
  user_id: number;
  package_id: number;
  addon_ids: number[];
  amount: number;
  status: 'approved' | 'pending' | 'failed' | 'refunded';
  payment_system: 'stripe' | 'paypal' | 'bank_transfer';
  source: 'on_boarding' | 'renewal' | 'upgrade' | 'manual';
  product: ProductBilling;

  external_charge_id: string;
  external_checkout_id: string;
  external_invoice_id: string;
  external_transaction_id: string;
  invoice_id: string;
  invoice_pdf: string;

  created_at: string;
  updated_at: string;
  deleted_at: string;
  paid_at: string;
}>;

export type BillingRenewCheckoutPayload = {
  package_id: number;
  addon_ids: number[];
  payment_gateway: string;
  addon_payment_handling_type?: 'monthly' | 'non_recurring' | null;
  coupon_code?: string;
  billing_info: Partial<PaymentFormValues>;
};

export type BillingRenewItemProduct = {
  id: number;
  title: string;
  display_name_app?: string;
  display_name_web?: string;
  description: string | null;
  price: number;
};

export type BillingRenewItemBillingPlan = {
  first_price?: number | null;
  addon_monthly_price?: number | null;
  second_price?: number | null;
  first_period_value?: number | null;
  first_period_unit?: string | null;
  second_period_value?: number | null;
  second_period_unit?: string | null;
  rebill_times_type?: string | null;
};

export type BillingRenewAddon = {
  product: BillingRenewItemProduct;
  billing_plan?: BillingRenewItemBillingPlan | null;
  payment_handling_type?: 'monthly' | 'non_recurring' | null;
};

export type BillingRenewPackage = {
  invoice_id: number;
  product: BillingRenewItemProduct;
  billing_plan?: BillingRenewItemBillingPlan | null;
  addons: BillingRenewAddon[];
};

export type SubscriptionAccessProduct = Partial<{
  id: number;
  title: string;
  display_name_app: string;
  display_name_web: string;
  category: string;
  type: string;
}>;

export type SubscriptionAccessRead = Partial<{
  access_id: number;
  invoice_item_id: number;
  begin_date: string;
  expire_date: string | null;
  is_active: boolean;
  qty: number;
  product: SubscriptionAccessProduct;
}>;

/**
 * New API shape for GET /billing/subscriptions/ (invoice-level, de-duplicated)
 */
export type InvoiceSubscriptionRead = Partial<{
  invoice_id: number;
  total_amount: number | string;
  invoice_status: BillingStatus;
  subscription_status: BillingStatus;
  is_active: boolean;
  is_recurring: boolean;
  payment_system: PaymentGateway;
  rebill_date: string | null;
  begin_date: string;
  expire_date: string | null;
  accesses: SubscriptionAccessRead[];
}>;
